import { activeLanguage } from '../i18n/language';
import { useOfferingMarkets, type OfferingMarkets } from '../hooks/useOfferingMarkets';
import { offeringCountryList } from '../lib/offeringNotice';
import { colors } from '../styles/theme';

/**
 * The one discreet line on a client page that shows products, figures or proposals: where the
 * offering IS made, and that it is not made anywhere else (NP 47-201, s. 2.2(2), which asks a
 * posted offer to name the jurisdictions it is made in and to leave the reader's out).
 *
 * The countries come from the country switch (`GET /offering/markets`), never from a list
 * written here: switching a country on or off changes the line. When the switch cannot be read
 * the line says it without naming a country, and nothing is shown while it is being read.
 */
export function OfferingNoticeView({ markets, unavailable }: Readonly<OfferingMarkets>): JSX.Element | null {
  let text: string | null = null;
  if (unavailable) {
    text = 'This offering is not made to residents of jurisdictions where it is not enabled.';
  } else if (markets !== null && markets.length === 0) {
    text = 'This offering is not currently made to residents of any jurisdiction.';
  } else if (markets !== null) {
    const names = offeringCountryList(markets, activeLanguage());
    text = `This offering is made only to residents of ${names}. It is not made to residents of any other jurisdiction.`;
  }
  if (text === null) return null;

  return (
    <p
      role="note"
      style={{ margin: 0, padding: '12px 0 0', fontSize: 12, color: colors.muted, lineHeight: 1.5 }}
    >
      {text}
    </p>
  );
}

export default function OfferingNotice(): JSX.Element | null {
  return <OfferingNoticeView {...useOfferingMarkets()} />;
}
